import { useState, useEffect, useCallback } from 'react';

const HISTORY_STORAGE_KEY = 'text-onl-history';
const MAX_HISTORY_ITEMS = 15;

export interface HistoryItem {
  text: string;
  timestamp: number;
}

export function useTextHistory() {
  const [history, setHistory] = useState<HistoryItem[]>([]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setHistory(parsed);
        }
      }
    } catch (error) {
      console.error('Failed to load text history:', error);
    }
  }, []);

  // Save history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('Failed to save text history:', error);
    }
  }, [history]);

  const addToHistory = useCallback((text: string) => {
    if (!text.trim()) return;

    setHistory(prev => {
      // Remove existing duplicate if present
      const filtered = prev.filter(item => item.text !== text);

      // Add new item at the beginning
      const newHistory = [
        { text, timestamp: Date.now() },
        ...filtered
      ].slice(0, MAX_HISTORY_ITEMS);

      return newHistory;
    });
  }, []);

  const removeFromHistory = useCallback((text: string) => {
    setHistory(prev => prev.filter(item => item.text !== text));
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  return {
    history,
    addToHistory,
    removeFromHistory,
    clearHistory,
    historyCount: history.length
  };
}
